import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { updateCrewProfile } from "store/crew-profiles"
import { toaster } from "components/common/Toast"

/**
 * Custom hook for managing crew member edit functionality
 * Provides modal state management and save logic with FormData creation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Optional callback after successful save
 * @returns {Object} Edit state and handlers
 */
export const useCrewMemberEdit = ({ onSuccess } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedCrewMember, setSelectedCrewMember] = useState(null)

  /**
   * Opens the edit modal for a specific crew member
   * @param {Object} crewMember - Crew member data to edit
   */
  const handleEdit = useCallback((crewMember) => {
    setSelectedCrewMember(crewMember)
    setIsEditModalOpen(true)
  }, [])

  /**
   * Closes the edit modal and clears selection
   */
  const handleCancel = useCallback(() => {
    setIsEditModalOpen(false)
    setSelectedCrewMember(null)
  }, [])

  /**
   * Creates FormData from form values
   * Maps UI format to API format
   * @param {Object} values - Form values from the modal
   * @returns {FormData} Formatted data for API
   */
  const createFormData = useCallback((values) => {
    const formData = new FormData()

    // Extract phone number and country code from phone object
    const phoneNumber = values.phoneNumber?.fullNumber || ""
    const countryCode = values.phoneNumber?.countryCode || "US"

    // Map form values to API format
    formData.append("profile[first_name]", values.firstName)
    formData.append("profile[last_name]", values.lastName)
    formData.append("profile[email]", values.email)
    formData.append("profile[phone_number]", phoneNumber)
    formData.append("profile[country_code]", countryCode)
    formData.append("profile[title]", values.title || "")
    formData.append("profile[role_id]", values.roleId)
    formData.append("profile[description]", values.description || "")

    // Handle photo upload if it's a File object
    if (values.photoFile) {
      formData.append("profile[photo]", values.photoFile)
    }

    return formData
  }, [])

  /**
   * Saves crew member edits
   * @param {Object} values - Form values from the modal
   * @param {string|number} crewMemberId - ID of the crew member to update
   */
  const handleSave = useCallback(async (values, crewMemberId) => {
    if (!crewMemberId) {
      console.error("No crew member ID provided for update")
      return
    }

    const formData = createFormData(values)

    try {
      const response = await dispatch(updateCrewProfile(crewMemberId, formData))
      toaster.success(response?.message || t("crewMember.detail.messages.updateSuccess"))
      setIsEditModalOpen(false)
      setSelectedCrewMember(null)

      // Call optional success callback (e.g., to refresh list)
      if (onSuccess) {
        onSuccess(crewMemberId)
      }
    } catch (error) {
      console.error(t("crewMember.detail.messages.updateError"), error)
      throw error
    }
  }, [dispatch, t, createFormData, onSuccess])

  return {
    // State
    isEditModalOpen,
    selectedCrewMember,
    // Handlers
    handleEdit,
    handleCancel,
    handleSave,
    // Utility
    createFormData
  }
}
